<script setup lang="ts">
  import { LazyModalGeneric, LazyModalGiftPlayMoney } from '#components';
  import CorePinInput from '~/components/core/CorePinInput.vue';
  import { useAppToast } from '~/composables/shared/useAppToast';
  import { stores } from '~/store';

  const KEYCODE_ENTER = 13;

  const { showToast } = useAppToast();
  const { fireSignUpPixel } = useGeniusMonkey();
  const route = useRoute();
  const { sendConversionRegistration } = useGtag();
  const { credentials } = storeToRefs(stores.signUp);
  const requestIsPending = ref(false);

  const otpCode = ref([]);
  const otpCodeString = computed(() => otpCode.value.join(''));

  const buttonDisabled = computed(() => otpCodeString.value.length !== 6 || requestIsPending.value);

  onMounted(async () => {
    window.addEventListener('keyup', checkSubmitKey);
    await init();
  });

  onBeforeUnmount(() => {
    window.removeEventListener('keyup', checkSubmitKey);
  });

  async function init() {
    stores.signUp.setSignUpInProcess(true);

    if (!stores.user.isLoggedIn) {
      const { $firebase } = useTypedNuxtApp();
      await $firebase.auth?.signInWithEmailAndPassword(credentials.value.email, credentials.value.password);
    }
  }

  async function submitCode() {
    if (requestIsPending.value) return;

    try {
      requestIsPending.value = true;

      const res = await stores.user.verifyEmailByCode(otpCodeString.value);

      if (res.success) {
        sendConversionRegistration();
        fireSignUpPixel();

        stores.signUp.setSignUpInProcess(false);
        stores.modals.close('sign-up');

        if (route.path !== '/lobby') {
          await navigateTo({ name: ERouteName.LOBBY });
        }

        stores.modals.open('gift-play-money', LazyModalGiftPlayMoney);
      } else {
        requestIsPending.value = false;

        showToast({
          title: 'Verification failed',
          description: res.message,
          color: 'error',
        });
      }
    } catch (e) {
      requestIsPending.value = false;

      console.error(e);

      showToast({
        title: 'Verification failed',
        description: e.message,
        color: 'error',
      });
    }
  }

  function checkSubmitKey(e: any) {
    if (e.keyCode !== KEYCODE_ENTER) return;

    submitCode();
  }

  async function resendVerificationCode() {
    const { success, message } = await stores.user.resendVerificationCode();

    await stores.modals.open('remove-friend', LazyModalGeneric, {
      title: success ? 'The code has been sent' : 'Something went wrong',
      text: message,
      actions: [{ label: 'OK', color: 'primary' }],
    });
  }
</script>

<template>
  <div class="email-verification-modal">
    <h2 class="text-white text-center font-barlow text-[32px] font-semibold leading-[100%]">✉️ Email Verification</h2>

    <p class="text-white/80 text-center font-barlow text-lg font-medium leading-[150%] my-6">
      We have sent the 6-digit verification code to {{ credentials.email }}. Check your email for the code and enter it
      below:
    </p>

    <CorePinInput v-model="otpCode" w-full />

    <CoreButton class="mt-4 w-full" loading-auto :disabled="buttonDisabled" @click="submitCode">Submit code</CoreButton>

    <div class="text-white/80 text-center font-barlow text-lg font-medium leading-[150%] mt-6">
      Still haven’t received?
      <span
        data-test-id="send-again-link"
        class="text-[#0080FF] font-barlow text-lg font-medium leading-[150%] cursor-pointer underline decoration-solid underline-offset-auto decoration-from-font underline-skip-ink-none"
        @click="resendVerificationCode"
      >
        Send code again
      </span>
    </div>
  </div>
</template>
